import React from "react";
import { useTable, usePagination } from "react-table";
import { motion } from "framer-motion";
import { BiServer } from "react-icons/bi";
import {
	Table,
	Thead,
	Tr,
	Th,
	Tbody,
	Td,
	Center,
	Text,
	Box,
	Flex,
	Tooltip,
	IconButton,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	NumberDecrementStepper,
	NumberIncrementStepper,
	Select,
} from "@chakra-ui/react";
import { ArrowRightIcon, ArrowLeftIcon, ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { Spinner } from "../Loader";
const MotionRow = motion(Tr);

export default function PaginationTable({ background = "gray.50", color = "gray.800", paginate = false, columns, queryPageIndex, queryPageSize, setQueryPageIndex, setQueryPageSize, ...props }) {
	let { data, isLoading } = props;

	const memorizedData = React.useMemo(() => (data ? data?.data : [{}]), [data]);

	const initialState = {
		queryPageIndex: 0,
		queryPageSize: 10,
		totalCount: null,
	};

	const PAGE_CHANGED = "PAGE_CHANGED";
	const PAGE_SIZE_CHANGED = "PAGE_SIZE_CHANGED";
	const TOTAL_COUNT_CHANGED = "TOTAL_COUNT_CHANGED";

	const reducer = (state, { type, payload }) => {
		switch (type) {
			case PAGE_CHANGED:
				return {
					...state,
					queryPageIndex: setQueryPageIndex(payload + 1),
				};
			case PAGE_SIZE_CHANGED:
				return {
					...state,
					queryPageSize: setQueryPageSize(payload),
				};
			case TOTAL_COUNT_CHANGED:
				return {
					...state,
					totalCount: payload,
				};
			default:
				throw new Error(`Unhandled action type: ${type}`);
		}
	};

	const [state, dispatch] = React.useReducer(reducer, initialState);
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		page,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		state: { pageIndex, pageSize },
	} = useTable(
		{
			columns,
			data: memorizedData || [],
			initialState: {
				pageIndex: state.queryPageIndex,
				pageSize: state.queryPageSize,
			},
			manualPagination: true,
			pageCount: Math.ceil(data?.pagination?.total / data?.pagination?.rowsPerPage),
		},
		usePagination
	);

	React.useEffect(() => {
		dispatch({ type: PAGE_CHANGED, payload: pageIndex });
	}, [pageIndex]);

	React.useEffect(() => {
		dispatch({ type: PAGE_SIZE_CHANGED, payload: pageSize });
		gotoPage(0);
	}, [pageSize, gotoPage]);

	React.useEffect(() => {
		if (data?.pagination?.total) {
			dispatch({
				type: TOTAL_COUNT_CHANGED,
				payload: data?.pagination?.total,
			});
		}
	}, [data?.pagination?.total]);
	return isLoading ? (
		<Spinner />
	) : (
		<>
			<Table {...getTableProps()} size="sm" variant="simple">
				<Thead zIndex="2" pos="sticky" top="0" bg={background}>
					{headerGroups.map((headerGroup) => (
						<Tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<Th fontWeight="bold" {...column.getHeaderProps()} color={color}>
									{column.render("Header")}
								</Th>
							))}
						</Tr>
					))}
				</Thead>
				{isLoading ? (
					<Spinner />
				) : (
					<>
						<Tbody {...getTableBodyProps()}>
							{rows.length > 0 ? (
								<>
									{page.map((row) => {
										prepareRow(row);
										return (
											<MotionRow
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
												transition={{ duration: 0.2 }}
												cursor="pointer"
												_hover={{ bg: "gray.50" }}
												{...row.getRowProps()}
											>
												{row.cells.map((cell) => (
													<Td style={{ cursor: "default" }} py="2" {...cell.getCellProps()}>
														{cell.render("Cell")}
													</Td>
												))}
											</MotionRow>
										);
									})}
								</>
							) : (
								<Tr>
									<Td colSpan={columns.length}>
										<Center py="1" w="full" h="full" color="gray.400">
											<BiServer size="1.1rem" /> <Text fontSize="md">&nbsp;No data.</Text>
										</Center>
									</Td>
								</Tr>
							)}
						</Tbody>
					</>
				)}
			</Table>
			<Flex alignItems="center" justify={"flex-end"} mt={5} mr={5}>
				<Select
					w={32}
					value={pageSize}
					onChange={(e) => {
						setPageSize(Number(e.target.value));
					}}
				>
					{[10, 20, 30, 40, 50, data?.pagination?.total].map((pageSize) => (
						<option key={pageSize} value={pageSize}>
							Show {pageSize}
						</option>
					))}
				</Select>
				<Box ml={4}>
					<Text flexShrink="0" mr={8}>
						Page{" "}
						<Text fontWeight="bold" as="span">
							{pageIndex + 1}
						</Text>{" "}
						of{" "}
						<Text fontWeight="bold" as="span">
							{pageOptions.length}
						</Text>
					</Text>
				</Box>
				<Text flexShrink="0">Go to page:</Text>{" "}
				<NumberInput
					ml={2}
					mr={8}
					w={28}
					min={1}
					max={pageOptions.length}
					onChange={(value) => {
						const page = value ? value - 1 : 0;
						gotoPage(page);
					}}
					defaultValue={pageIndex + 1}
				>
					<NumberInputField />
					<NumberInputStepper>
						<NumberIncrementStepper />
						<NumberDecrementStepper />
					</NumberInputStepper>
				</NumberInput>
				<Box>
					<Tooltip label="First Page">
						<IconButton onClick={() => gotoPage(0)} isDisabled={!canPreviousPage} icon={<ArrowLeftIcon h={3} w={3} />} mr={4} />
					</Tooltip>
					<Tooltip label="Previous Page">
						<IconButton onClick={previousPage} isDisabled={!canPreviousPage} icon={<ChevronLeftIcon h={6} w={6} />} mr={4} />
					</Tooltip>
					<Tooltip label="Next Page">
						<IconButton onClick={nextPage} isDisabled={!canNextPage} icon={<ChevronRightIcon h={6} w={6} />} />
					</Tooltip>
					<Tooltip label="Last Page">
						<IconButton onClick={() => gotoPage(pageCount - 1)} isDisabled={!canNextPage} icon={<ArrowRightIcon h={3} w={3} />} ml={4} />
					</Tooltip>
				</Box>
			</Flex>
		</>
	);
}
